import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, ChefHat } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

const EMPTY_INGREDIENT = { name: "", quantity: "", unit: "", optional: false };
const EMPTY_STEP = { n: 1, text: "", tip: "" };

export default function RecipeSubmit() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: "", short_description: "", story: "",
    category: "Main Courses", cuisine: "Traditional", country: "", region: "",
    prep_time_min: 15, cook_time_min: 30, rest_time_min: 0,
    servings: 4, difficulty: "Beginner",
    ingredients: [{ ...EMPTY_INGREDIENT }],
    steps: [{ ...EMPTY_STEP }],
    cover_photo: null,
    diet_flags: [],
    equipment: [],
    tags: "",
    website_url: "",
    video_url: "",
    cooking_tips: "",
  });

  useEffect(() => { http.get("/recipes/config").then((r) => setConfig(r.data)); }, []);

  if (!user) return <Navigate to="/login?next=/recipes/submit" replace />;
  if (!config) return <div className="max-w-3xl mx-auto py-16 px-6" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const addIngredient = () => set("ingredients", [...f.ingredients, { ...EMPTY_INGREDIENT }]);
  const removeIngredient = (i) => set("ingredients", f.ingredients.filter((_, idx) => idx !== i));
  const setIngredient = (i, k, v) => {
    const next = [...f.ingredients]; next[i] = { ...next[i], [k]: v }; set("ingredients", next);
  };
  const addStep = () => set("steps", [...f.steps, { n: f.steps.length + 1, text: "", tip: "" }]);
  const removeStep = (i) => set("steps", f.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, n: idx + 1 })));
  const setStep = (i, k, v) => {
    const next = [...f.steps]; next[i] = { ...next[i], [k]: v }; set("steps", next);
  };
  const toggleFlag = (flag) => {
    set("diet_flags", f.diet_flags.includes(flag) ? f.diet_flags.filter((x) => x !== flag) : [...f.diet_flags, flag]);
  };

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Image must be under 4 MB");
    set("cover_photo", await toBase64(file));
  };

  const submit = async () => {
    if (!f.title.trim() || !f.country.trim()) return toast.error("Title and country are required");
    if (f.ingredients.filter((i) => i.name.trim()).length === 0) return toast.error("Add at least one ingredient");
    if (f.steps.filter((s) => s.text.trim()).length === 0) return toast.error("Add at least one preparation step");
    setSaving(true);
    try {
      const payload = {
        ...f,
        prep_time_min: Number(f.prep_time_min) || 0,
        cook_time_min: Number(f.cook_time_min) || 0,
        rest_time_min: Number(f.rest_time_min) || 0,
        servings: Number(f.servings) || 4,
        ingredients: f.ingredients.filter((i) => i.name.trim()),
        steps: f.steps.filter((s) => s.text.trim()).map((s, idx) => ({ ...s, n: idx + 1 })),
        tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
        cooking_tips: f.cooking_tips.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await http.post("/recipes", payload);
      toast.success(data.status === "published" ? "Recipe published" : "Recipe submitted for review");
      nav(data.status === "published" ? `/recipes/${data.slug}` : "/recipes/mine");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="recipe-submit-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
        <ChefHat size={13} className="inline mr-1.5 -mt-0.5" />
        SHARE YOUR RECIPE
      </div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight mb-3">Add a recipe.</h1>
      <p className="text-sm mb-8" style={{ color: "var(--wofy-text-soft)" }}>
        Preserve a family favourite, a regional specialty, or a modern creation.
        Recipes are reviewed by our team before appearing publicly.
      </p>

      {/* Basics */}
      <section className="mb-8 border rounded-lg p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <h2 className="font-display text-xl mb-4">Basics</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="text-sm">Title
            <input data-testid="recipe-title" className="border rounded px-3 py-2 w-full mt-1" value={f.title} onChange={(e) => set("title", e.target.value)} />
          </label>
          <label className="text-sm">Country
            <input data-testid="recipe-country" className="border rounded px-3 py-2 w-full mt-1" placeholder="e.g. Italy" value={f.country} onChange={(e) => set("country", e.target.value)} />
          </label>
          <label className="text-sm">Region (optional)
            <input className="border rounded px-3 py-2 w-full mt-1" value={f.region} onChange={(e) => set("region", e.target.value)} />
          </label>
          <label className="text-sm">Cuisine
            <select data-testid="recipe-cuisine" className="border rounded px-3 py-2 w-full mt-1" value={f.cuisine} onChange={(e) => set("cuisine", e.target.value)}>
              {config.cuisines.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm">Category
            <select data-testid="recipe-category" className="border rounded px-3 py-2 w-full mt-1" value={f.category} onChange={(e) => set("category", e.target.value)}>
              {config.categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm">Difficulty
            <select className="border rounded px-3 py-2 w-full mt-1" value={f.difficulty} onChange={(e) => set("difficulty", e.target.value)}>
              {config.difficulties.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm col-span-2">Short description (1-2 sentences)
            <input data-testid="recipe-desc" className="border rounded px-3 py-2 w-full mt-1" value={f.short_description} onChange={(e) => set("short_description", e.target.value)} />
          </label>
        </div>
      </section>

      {/* Times / servings */}
      <section className="mb-8 border rounded-lg p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <h2 className="font-display text-xl mb-4">Time & servings</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="text-sm">Prep (min)<input type="number" className="border rounded px-3 py-2 w-full mt-1" value={f.prep_time_min} onChange={(e) => set("prep_time_min", e.target.value)} /></label>
          <label className="text-sm">Cook (min)<input type="number" className="border rounded px-3 py-2 w-full mt-1" value={f.cook_time_min} onChange={(e) => set("cook_time_min", e.target.value)} /></label>
          <label className="text-sm">Rest (min)<input type="number" className="border rounded px-3 py-2 w-full mt-1" value={f.rest_time_min} onChange={(e) => set("rest_time_min", e.target.value)} /></label>
          <label className="text-sm">Servings<input type="number" className="border rounded px-3 py-2 w-full mt-1" value={f.servings} onChange={(e) => set("servings", e.target.value)} /></label>
        </div>
      </section>

      {/* Photo */}
      <section className="mb-8 border rounded-lg p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <h2 className="font-display text-xl mb-4">Cover photo</h2>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer text-sm" style={{ borderColor: "var(--wofy-line)" }}>
            <Upload size={13} /> Upload
            <input data-testid="recipe-cover" type="file" accept="image/*" className="hidden" onChange={handleCover} />
          </label>
          {f.cover_photo && <img src={f.cover_photo} alt="preview" className="h-24 rounded" />}
        </div>
      </section>

      {/* Ingredients */}
      <section className="mb-8 border rounded-lg p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl">Ingredients</h2>
          <button data-testid="recipe-add-ingredient" onClick={addIngredient} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--wofy-primary)" }}><Plus size={13} /> Add</button>
        </div>
        <ul className="space-y-2">
          {f.ingredients.map((ing, idx) => (
            <li key={idx} className="grid grid-cols-[80px_60px_1fr_28px] gap-2">
              <input placeholder="Qty" className="border rounded px-2 py-1 text-sm" value={ing.quantity} onChange={(e) => setIngredient(idx, "quantity", e.target.value)} />
              <input placeholder="Unit" className="border rounded px-2 py-1 text-sm" value={ing.unit} onChange={(e) => setIngredient(idx, "unit", e.target.value)} />
              <input placeholder="Ingredient" className="border rounded px-2 py-1 text-sm" value={ing.name} onChange={(e) => setIngredient(idx, "name", e.target.value)} />
              <button onClick={() => removeIngredient(idx)} disabled={f.ingredients.length <= 1} className="text-xs opacity-70 hover:opacity-100 disabled:opacity-20" title="Remove"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      </section>

      {/* Steps */}
      <section className="mb-8 border rounded-lg p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl">Preparation steps</h2>
          <button data-testid="recipe-add-step" onClick={addStep} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--wofy-primary)" }}><Plus size={13} /> Add step</button>
        </div>
        <ol className="space-y-3">
          {f.steps.map((s, i) => (
            <li key={i} className="grid grid-cols-[24px_1fr_28px] gap-2 items-start">
              <span className="text-xs font-bold mt-2">{i + 1}.</span>
              <textarea placeholder="Describe this step…" rows={2} className="border rounded px-2 py-1 text-sm w-full" value={s.text} onChange={(e) => setStep(i, "text", e.target.value)} />
              <button onClick={() => removeStep(i)} disabled={f.steps.length <= 1} className="text-xs opacity-70 hover:opacity-100 disabled:opacity-20 mt-2" title="Remove"><Trash2 size={14} /></button>
            </li>
          ))}
        </ol>
      </section>

      {/* Diet / equipment */}
      <section className="mb-8 border rounded-lg p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <h2 className="font-display text-xl mb-4">Diet & extras</h2>
        <div className="text-sm mb-2 font-semibold">Diet flags</div>
        <div className="flex gap-2 flex-wrap mb-4">
          {config.diet_flags.map((flag) => (
            <button
              key={flag}
              onClick={() => toggleFlag(flag)}
              className="text-xs px-3 py-1 rounded-full border"
              style={{
                borderColor: "var(--wofy-line)",
                background: f.diet_flags.includes(flag) ? "var(--wofy-primary)" : "transparent",
                color: f.diet_flags.includes(flag) ? "white" : "var(--wofy-text)",
              }}
            >
              {flag.replace("_", " ")}
            </button>
          ))}
        </div>
        <label className="text-sm block mb-3">Tips (one per line)
          <textarea rows={3} className="border rounded px-3 py-2 w-full mt-1 text-sm" value={f.cooking_tips} onChange={(e) => set("cooking_tips", e.target.value)} />
        </label>
        <label className="text-sm block mb-3">Tags (comma-separated)
          <input className="border rounded px-3 py-2 w-full mt-1 text-sm" placeholder="italian, weeknight, quick" value={f.tags} onChange={(e) => set("tags", e.target.value)} />
        </label>
        <label className="text-sm block">The story behind this recipe (optional)
          <textarea rows={4} className="border rounded px-3 py-2 w-full mt-1 text-sm" placeholder="Family history, who taught you, why it matters…" value={f.story} onChange={(e) => set("story", e.target.value)} />
        </label>
      </section>

      <div className="flex gap-3 justify-end">
        <button onClick={() => nav("/recipes")} className="px-5 py-2.5 rounded-full border text-sm font-semibold" style={{ borderColor: "var(--wofy-line)" }}>Cancel</button>
        <button data-testid="recipe-submit-save" onClick={submit} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm" style={{ background: "var(--wofy-primary)", color: "white" }}>
          <Save size={14} /> {saving ? "Saving…" : "Submit recipe"}
        </button>
      </div>
    </div>
  );
}
